import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  HStack,
  Input,
  RefreshButton,
  Select,
  Table,
  Text,
  Textarea,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  feedbackService,
  FeedbackTriageStatus,
  FeedbackVoteType,
  ResponseFeedbackDetail,
  ResponseFeedbackListItem,
} from "services/feedbackService";
import { logsService } from "services/logsService";
import LogDetails from "views/admin/logs/details";

const Feedback = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ResponseFeedbackListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] =
    useState<ResponseFeedbackDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailAiLogId, setDetailAiLogId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  const [days, setDays] = useState<number>(7);
  const [voteFilter, setVoteFilter] = useState<"all" | FeedbackVoteType>("all");
  const [triageFilter, setTriageFilter] = useState<
    "all" | FeedbackTriageStatus
  >("all");
  const [reasonFilter, setReasonFilter] = useState("");
  const [reasonInput, setReasonInput] = useState("");

  const [triageStatus, setTriageStatus] = useState<FeedbackTriageStatus>("New");
  const [internalNotes, setInternalNotes] = useState("");
  const [savingTriage, setSavingTriage] = useState(false);

  const totalPages = Math.ceil(total / pageSize);

  const loadList = async () => {
    try {
      setLoading(true);
      const result = await feedbackService.getFeedback({
        page,
        pageSize,
        vote: voteFilter,
        triageStatus: triageFilter,
        reasonTag: reasonFilter || undefined,
        days,
      });
      setItems(result.items);
      setTotal(result.total);
      setError("");

      if (result.items.length > 0 && !selectedId) {
        setSelectedId(result.items[0].id);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.feedback.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id: string) => {
    try {
      setDetailLoading(true);
      const detail = await feedbackService.getFeedbackById(id);
      setSelectedDetail(detail);
      setTriageStatus(detail.triageStatus);
      setInternalNotes(detail.internalNotes || "");
      setDetailAiLogId(null);

      if (detail.chatSessionId && detail.responseId) {
        try {
          const sessionLogs = await logsService.getLogsBySession(
            detail.chatSessionId,
          );
          const matchedLog = sessionLogs.find(
            (log) => log.responseId === detail.responseId,
          );
          setDetailAiLogId(matchedLog?.id || null);
        } catch {
          setDetailAiLogId(null);
        }
      }

      setError("");
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.feedback.detailLoadFailed"));
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, voteFilter, triageFilter, reasonFilter, days]);

  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const saveTriage = async () => {
    if (!selectedDetail) return;

    try {
      setSavingTriage(true);
      await feedbackService.updateTriageStatus(
        selectedDetail.id,
        triageStatus,
        internalNotes || undefined,
      );
      await loadDetail(selectedDetail.id);
      await loadList();
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.feedback.triageSaveFailed"));
    } finally {
      setSavingTriage(false);
    }
  };

  return (
    <Flex direction="column" gap={5} w="full">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Heading size="2xl">{t("app.feedback.title")}</Heading>
        <HStack gap={3}>
          <AskStrikeUpButton context={t("app.askStrikeUp.context.feedback")} />
          <RefreshButton onClick={loadList} loading={loading} />
        </HStack>
      </Flex>

      {error && (
        <Box borderRadius="lg" bg="red.100" p={3}>
          <Text fontSize="sm" color="red.700">
            {error}
          </Text>
        </Box>
      )}

      <Card.Root>
        <Card.Body>
          <HStack gap={3} wrap="wrap">
            <Select
              value={[String(days)]}
              onValueChange={(e: any) => {
                setDays(parseInt(e.value[0], 10));
                setPage(1);
              }}
              items={[
                { value: "7", label: t("app.dashboard.timeRange.last7Days") },
                { value: "14", label: t("app.dashboard.timeRange.last14Days") },
              ]}
              w="170px"
            />
            <Select
              value={[voteFilter]}
              onValueChange={(e: any) => {
                setVoteFilter(
                  (e.value[0] as "all" | FeedbackVoteType) || "all",
                );
                setPage(1);
              }}
              items={[
                { value: "all", label: t("app.feedback.filters.allVotes") },
                {
                  value: "Positive",
                  label: t("app.feedback.filters.positiveOnly"),
                },
                {
                  value: "Negative",
                  label: t("app.feedback.filters.negativeOnly"),
                },
              ]}
              w="180px"
            />
            <Select
              value={[triageFilter]}
              onValueChange={(e: any) => {
                setTriageFilter(
                  (e.value[0] as "all" | FeedbackTriageStatus) || "all",
                );
                setPage(1);
              }}
              items={[
                { value: "all", label: t("app.feedback.filters.allTriage") },
                { value: "New", label: t("app.feedback.triage.new") },
                { value: "Reviewed", label: t("app.feedback.triage.reviewed") },
                { value: "Actioned", label: t("app.feedback.triage.actioned") },
                { value: "Ignored", label: t("app.feedback.triage.ignored") },
              ]}
              w="190px"
            />
            <Input
              placeholder={t("app.feedback.filters.reasonPlaceholder")}
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  setReasonFilter(reasonInput);
                  setPage(1);
                }
              }}
              w="260px"
            />
            <Button
              onClick={() => {
                setReasonFilter(reasonInput);
                setPage(1);
              }}
            >
              {t("app.common.search")}
            </Button>
          </HStack>
        </Card.Body>
      </Card.Root>

      <Grid templateColumns={{ base: "1fr", xl: "1.3fr 1fr" }} gap={4}>
        <Card.Root>
          <Card.Body p={0} overflow="hidden">
            {loading ? (
              <Flex justify="center" align="center" minH="200px">
                <Text>{t("app.common.loading")}</Text>
              </Flex>
            ) : items.length === 0 ? (
              <Flex justify="center" align="center" minH="200px">
                <Text>{t("app.feedback.noData")}</Text>
              </Flex>
            ) : (
              <Box w="full" overflowX="auto">
                <Table.Root variant="outline" size="sm" w="full" minW="760px">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>
                        {t("app.feedback.table.vote")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.feedback.table.reason")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.feedback.table.triage")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.feedback.table.response")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.feedback.table.date")}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {items.map((item) => (
                      <Table.Row
                        key={item.id}
                        cursor="pointer"
                        bg={
                          selectedId === item.id
                            ? { base: "blue.50", _dark: "whiteAlpha.100" }
                            : undefined
                        }
                        onClick={() => setSelectedId(item.id)}
                      >
                        <Table.Cell>
                          <Badge
                            colorPalette={
                              item.voteType === "Positive" ? "green" : "red"
                            }
                          >
                            {item.voteType === "Positive" ? "👍" : "👎"}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>{item.reasonTag || "-"}</Table.Cell>
                        <Table.Cell>{item.triageStatus}</Table.Cell>
                        <Table.Cell maxW="400px">
                          <Text fontSize="sm" lineClamp={2}>
                            {item.assistantMessagePreview || "-"}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm" whiteSpace="nowrap">
                            {new Date(item.submittedAt).toLocaleString()}
                          </Text>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Body>
            {detailLoading ? (
              <Text>{t("app.common.loading")}</Text>
            ) : !selectedDetail ? (
              <Text>{t("app.feedback.selectRow")}</Text>
            ) : (
              <Flex direction="column" gap={3}>
                <HStack justify="space-between">
                  <Badge
                    colorPalette={
                      selectedDetail.voteType === "Positive" ? "green" : "red"
                    }
                  >
                    {selectedDetail.voteType === "Positive"
                      ? t("app.feedback.vote.positive")
                      : t("app.feedback.vote.negative")}
                  </Badge>
                  <Text fontSize="xs" color="gray.500">
                    {new Date(selectedDetail.submittedAt).toLocaleString()}
                  </Text>
                </HStack>

                <Text fontSize="sm" fontWeight="semibold">
                  {t("app.feedback.details.aiResponse")}
                </Text>
                {detailAiLogId ? (
                  <Box
                    borderWidth="1px"
                    borderColor={{ base: "gray.200", _dark: "gray.700" }}
                    borderRadius="md"
                    p={2}
                  >
                    <LogDetails logId={detailAiLogId} embedded />
                  </Box>
                ) : (
                  <Textarea
                    rows={8}
                    value={
                      selectedDetail.assistantMessageContent ||
                      selectedDetail.assistantMessagePreview ||
                      ""
                    }
                    readOnly
                  />
                )}

                <Text fontSize="sm" fontWeight="semibold">
                  {t("app.feedback.details.userFeedback")}
                </Text>
                <Text fontSize="sm">{selectedDetail.reasonTag || "-"}</Text>
                {selectedDetail.reasonText && (
                  <Text fontSize="sm">{selectedDetail.reasonText}</Text>
                )}

                <Select
                  label={t("app.feedback.details.triageStatus")}
                  value={[triageStatus]}
                  onValueChange={(e: any) =>
                    setTriageStatus(
                      (e.value[0] as FeedbackTriageStatus) || "New",
                    )
                  }
                  items={[
                    { value: "New", label: t("app.feedback.triage.new") },
                    {
                      value: "Reviewed",
                      label: t("app.feedback.triage.reviewed"),
                    },
                    {
                      value: "Actioned",
                      label: t("app.feedback.triage.actioned"),
                    },
                    {
                      value: "Ignored",
                      label: t("app.feedback.triage.ignored"),
                    },
                  ]}
                />

                <Textarea
                  label={t("app.feedback.details.internalNotes")}
                  rows={4}
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                />

                <Button onClick={saveTriage} loading={savingTriage}>
                  {t("app.feedback.details.saveTriage")}
                </Button>
              </Flex>
            )}
          </Card.Body>
        </Card.Root>
      </Grid>

      {total > 0 && (
        <HStack justify="space-between">
          <Text fontSize="sm" color={{ base: "gray.600", _dark: "gray.400" }}>
            {t("app.common.showing")} {(page - 1) * pageSize + 1} -{" "}
            {Math.min(page * pageSize, total)} {t("app.common.of")} {total}
          </Text>
          <HStack>
            <Button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              size="sm"
            >
              {t("app.common.previous")}
            </Button>
            <Text fontSize="sm">
              {t("app.common.page")} {page} {t("app.common.of")} {totalPages}
            </Text>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              size="sm"
            >
              {t("app.common.next")}
            </Button>
          </HStack>
        </HStack>
      )}
    </Flex>
  );
};

export default Feedback;
